import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';

import { HippoComponentProps } from '../index';
import Badge from '../Badge/Badge';
import BadgeDot from '../Badge/BadgeDot';
import Icon from '../Icon/Icon';
import styles from './MapControlFloorSelector.module.scss';

export interface Floor {
  id: number;
  name: string;
  disabled?: boolean;
  value?: number;
}

export interface Building {
  id: number;
  floors: Floor[];
  name: string;
  disabled?: boolean;
}

export interface Props extends HippoComponentProps {
  selectedFloorId?: number;
  selectedBuildingId?: number;
  buildings: Building[];
  placeholder?: string;
  anchor?: 'start' | 'centre' | 'end';
  compact?: boolean;
  disabled?: boolean;
  locateCurrentFloorText?: string;
  locateCurrentFloor?: () => void;
  onSelectFloor?: (floor: Floor) => void;
  onSelectBuilding?: (building: Building) => void;
}

const MapControlFloorSelector: React.FC<Props> = ({
  dataQA,
  className,
  selectedFloorId,
  selectedBuildingId,
  buildings,
  placeholder = 'Find a building',
  anchor = 'start',
  compact = false,
  disabled = false,
  locateCurrentFloorText = 'What floor am I on?',
  locateCurrentFloor,
  onSelectFloor,
  onSelectBuilding,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(
    null
  );
  const [triggerText, setTriggerText] = useState('');

  useEffect(() => {
    if (selectedFloorId || selectedBuildingId) {
      const building = buildings.find((building) => {
        // select building by id
        if (selectedBuildingId) return building.id === selectedBuildingId;
        // else select first building with selected floor id
        else
          return building.floors.some((floor) => floor.id === selectedFloorId);
      });

      const floor = building?.floors.find((floor) => {
        return floor.id === selectedFloorId;
      });

      // no condition required for just floor as it requires building to be defined
      if (building && floor) {
        setSelectedBuilding(building);
        setSelectedFloor(floor);
        setTriggerText(floor.name);
      } else if (building) {
        setSelectedBuilding(building);
        setSelectedFloor(null);
        setTriggerText(building.name);
      } else {
        setSelectedBuilding(null);
        setSelectedFloor(null);
        setTriggerText(
          buildings.length === 1 ? buildings[0].name : placeholder
        );
      }
    } else {
      setSelectedFloor(null);
      setSelectedBuilding(null);
      setTriggerText(buildings.length === 1 ? buildings[0].name : placeholder);
    }
  }, [selectedFloorId, selectedBuildingId, placeholder, buildings]);

  useEffect(() => {
    if (selectedFloor) {
      setTriggerText(selectedFloor.name);
    } else if (selectedBuilding) {
      setTriggerText(selectedBuilding.name);
    } else {
      setTriggerText(buildings.length === 1 ? buildings[0].name : placeholder);
    }
  }, [selectedFloor, selectedBuilding]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const triggerClicked = triggerRef.current?.contains(event.target as Node);
      const menuClicked = menuRef.current?.contains(event.target as Node);

      if (!(triggerClicked || menuClicked)) setOpen(false);
    }

    function handleEscapeClick(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeClick);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeClick);
    };
  }, [triggerRef, menuRef]);

  const showBadgeDot = buildings.some((building) =>
    building.floors.some((floor) => typeof floor.value === 'number')
  );

  function toggleOpen() {
    // if only one building and no floor selected, opening the floor selector should select the building
    if (open === false && buildings.length === 1 && selectedFloor === null) {
      setSelectedBuilding(buildings[0]);
      onSelectBuilding?.(buildings[0]);
    }

    setOpen((o) => !o);
  }

  return (
    <div data-qa={dataQA} className={classNames(styles.container, className)}>
      {/* TRIGGER COMPONENT */}
      <div
        ref={triggerRef}
        data-qa={`${dataQA}-trigger`}
        className={classNames(styles.trigger, className, {
          [styles.disabled]: disabled,
          [styles[`anchor-${anchor}`]]: anchor,
        })}
        onClick={toggleOpen}
      >
        {showBadgeDot && (
          <BadgeDot
            dataQA="floorName"
            className={styles.badgeDot}
            type="search"
            size="small"
          />
        )}
        {triggerText}
        <Icon
          dataQA="unfold"
          className={styles.unfoldIcon}
          name="unfold_more"
          size={24}
        />
      </div>

      {open && buildings.length > 0 && (
        <div ref={menuRef} className={styles.menu}>
          {/* LOCATE CURRENT FLOOR ROW */}
          {locateCurrentFloor && (
            <>
              <div
                className={classNames(styles.row, styles.currentFloorLocator, {
                  [styles.compact]: compact,
                })}
                onClick={() => {
                  setOpen(false);
                  locateCurrentFloor();
                }}
              >
                <Icon
                  dataQA="near-me"
                  className={classNames(styles.leadingIcon, styles.nearMeIcon)}
                  name="near_me_sparkle"
                  size={24}
                />

                {locateCurrentFloorText}
              </div>
              <div className={styles.divider} />
            </>
          )}

          {/* FLOORS WHEN ONLY ONE BUILDING */}
          {buildings.length === 1 &&
            buildings[0].floors.length > 0 &&
            buildings[0].floors.map((floor) => (
              <div
                key={`floor-${floor.id}`}
                className={classNames(styles.row, styles.floor, {
                  [styles.compact]: compact,
                  [styles.active]:
                    floor.id === selectedFloor?.id && !floor.disabled,
                  [styles.disabled]: floor.disabled,
                })}
                onClick={() => {
                  setSelectedFloor(floor);
                  onSelectFloor?.(floor);
                  setOpen(false);
                }}
              >
                {floor.name}
                {floor.value && (
                  <Badge
                    dataQA={`floor-badge-${floor.id}`}
                    className={styles.badge}
                    value={floor.value}
                    type="search"
                  />
                )}
              </div>
            ))}

          {/* FLOORS WHEN MULTIPLE BUILDINGS */}
          {buildings.length > 1 &&
            buildings.map((building) => {
              const isSelectedBuilding = building.id === selectedBuilding?.id;
              const buildingValue = building.floors.reduce(
                (value, floor) => value + (floor.value || 0),
                0
              );

              return (
                <React.Fragment key={`building-${building.id}`}>
                  <div
                    className={classNames(styles.row, styles.building, {
                      [styles.compact]: compact,
                      [styles.active]: isSelectedBuilding && !building.disabled,
                      [styles.disabled]: building.disabled,
                    })}
                    onClick={() => {
                      if (selectedBuilding?.id !== building.id) {
                        setSelectedBuilding(building);
                        onSelectBuilding?.(building);
                      } else {
                        setSelectedBuilding(null);
                        setSelectedFloor(null);
                      }
                    }}
                  >
                    <Icon
                      dataQA="chevron"
                      className={styles.leadingIcon}
                      name={
                        isSelectedBuilding ? 'chevron_down' : 'chevron_right'
                      }
                      size={24}
                    />
                    {building.name}
                    {buildingValue > 0 && (
                      <Badge
                        dataQA={`building-badge-${building.id}`}
                        className={styles.badge}
                        value={buildingValue}
                        type="search"
                      />
                    )}
                  </div>

                  {isSelectedBuilding &&
                    building.floors.map((floor) => (
                      <div
                        key={`floor-${floor.id}`}
                        className={classNames(styles.row, styles.floor, {
                          [styles.compact]: compact,
                          [styles.active]:
                            floor.id === selectedFloor?.id && !floor.disabled,
                          [styles.disabled]: floor.disabled,
                        })}
                        onClick={() => {
                          setSelectedFloor(floor);
                          onSelectFloor?.(floor);
                          setOpen(false);
                        }}
                      >
                        {floor.name}
                        {floor.value && (
                          <Badge
                            dataQA={`floor-badge-${floor.id}`}
                            className={styles.badge}
                            value={floor.value}
                            type="search"
                          />
                        )}
                      </div>
                    ))}
                  <div className={styles.divider} />
                </React.Fragment>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default MapControlFloorSelector;
